import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import axiosObj from './config';
import { handleQuery } from '../utils/request';

export const DASHBOARD_API = `${process.env.API_URL}/dashboard/graphql`;
// export const DASHBOARD_API = `http://0.0.0.0:3000/graphql`;

const getAccessTokenFromURL = () => {
  const query = new URLSearchParams(window.location.search);
  const token = query.get('accessToken')
  if (token) {
    return token
  }
  return null
}

const getVisoDevicesFromURL = () => {
  const query = new URLSearchParams(window.location.search);
  const deviceNames = query.get('deviceNames')
  if (deviceNames) {
    const visoDevices = deviceNames.split(',')
    return visoDevices
  }
  return null
}

const METRIC_DATA = {
  _id: true,
  name: true,
  archived: true,
};

const RAW_CHART_DATA = {
  label: true,
  result: true
}

const CHART_DATA = {
  metricsCount: true,
  groupBy: true,
  metricColumns: true,
  metricsData: {
    group: true,
    values: true
  }
};

const DASHBOARD_DETAILS_VALUES = {
  _id: true,
  name: true,
  description: true,
  createdBy: {
    _id: true,
    firstName: true,
    lastName: true,
  },
  createdAt: true,
  application: {
    _id: true,
    name: true,
  },
  configuration: true,
  tags: true,
  locations: {
    _id: true,
    name: true,
  },
  charts: {
    id: true,
    type: true,
    title: true,
    tableName: true,
    source: true,
    metricColumns: true,
    createdBy: {
      _id: true,
    },
    dashboard: {
      _id: true
    },
    configuration: true,
    timeRangeConfig: {
      useDashboardDefault: true
    }
  },
  queryMode: true,
  aiContext: {
    usage: true,
    decisions: true,
    usageDetails: true
  }
};

const CHART_DETAILS_VALUES = {
  id: true,
  type: true,
  title: true,
  tableName: true,
  source: true,
  metricColumns: true,
  createdBy: {
    _id: true,
  },
  dashboard: {
    _id: true
  },
  configuration: true,
};

export function getDashboardTags(args) {
  const query = {
    query: {
      dashboardTags: {
        __args: args
      }
    }
  }
  return handleQuery(query, DASHBOARD_API)
}

export function updateDashboardTags(args) {
  const query = {
    mutation: {
      bulkUpdateDashboardTags: {
        __args: args,
      },
    },
  }
  return handleQuery(query, DASHBOARD_API)
}

export function getDashboard(params) {
  const reportDashboardId = localStorage.getItem('reportDashboardId')
  const reportAccessToken = localStorage.getItem('reportDashboardAccesToken')
  localStorage.removeItem('reportDashboardId')
  localStorage.removeItem('reportDashboardAccesToken')

  const dashboardId = params.id
  const isReportDashboard = !(!!dashboardId) && !!reportDashboardId && !!reportAccessToken

  const query = {
    query: {
      dashboard: {
        __args: params,
        ...DASHBOARD_DETAILS_VALUES,
      },
    },
  };

  if (isReportDashboard) {
    query.query.dashboard.__args = {
      ...query.query.dashboard.__args,
      id: reportDashboardId
    }
  }
  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  const requestParams = {
    method: 'POST',
    url: DASHBOARD_API,
    data: {
      variables: {},
      query: gqlQuery,
    },
  }

  if (isReportDashboard) {
    requestParams.headers = {
      'content-type': 'application/json',
      authorization: reportAccessToken ? `Bearer ${reportAccessToken}` : undefined,
    }
  }

  return axiosObj(requestParams);
}

export function createDashboard(args) {
  const query = {
    mutation: {
      createDashboard: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DASHBOARD_API)
}

export function updateDashboard(args) {
  const query = {
    mutation: {
      updateDashboard: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DASHBOARD_API)
}

export function getMetrics(params) {
  const query = {
    query: {
      metrics: {
        count: true,
        data: {
          tableName: true,
          metrics: {
            name: true,
          }
        }
      },
    },
  };
  return handleQuery(query, DASHBOARD_API)
}

export function createChart(args) {
  const query = {
    mutation: {
      createChart: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DASHBOARD_API)
}

export function chartData(params) {
  const query = {
    query: {
      chartData: {
        __args: params,
        ...CHART_DATA,
      },
    },
  };

  const reportVisoDevices = getVisoDevicesFromURL()
  if (reportVisoDevices) {
    query.query.chartData.__args = {
      ...query.query.chartData.__args,
      visoDevices: reportVisoDevices
    }
  }

  const gqlQuery = jsonToGraphQLQuery(query, { pretty: true });

  const requestParams = {
    method: 'POST',
    url: DASHBOARD_API,
    data: {
      variables: {},
      query: gqlQuery,
    },
  }

  const reportAccessToken = getAccessTokenFromURL()
  if (reportAccessToken) {
    requestParams.headers = {
      'content-type': 'application/json',
      authorization: `Bearer ${reportAccessToken}`
    }
  }

  return axiosObj(requestParams);
}

export function chartDataPreview(params) {
  const query = {
    query: {
      previewData: {
        __args: params,
        ...CHART_DATA,
      },
    },
  };
  return handleQuery(query, DASHBOARD_API)
}

export function dashboards(params) {
  const query = {
    query: {
      dashboards: {
        __args: params,
        count: true,
        data: DASHBOARD_DETAILS_VALUES,
      },
    },
  };
  return handleQuery(query, DASHBOARD_API)
}

export function updateChart(args) {
  const query = {
    mutation: {
      updateChart: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DASHBOARD_API)
}

export function deleteChart(args) {
  const query = {
    mutation: {
      deleteChart: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DASHBOARD_API)
}

export function deleteDashboard(args) {
  const query = {
    mutation: {
      deleteDashboard: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DASHBOARD_API)
}

export function deleteDashboards(args) {
  const query = {
    mutation: {
      deleteDashboards: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DASHBOARD_API)
}

export function duplicateDashboard(args) {
  const query = {
    mutation: {
      duplicateDashboard: {
        __args: args,
      },
    },
  };
  return handleQuery(query, DASHBOARD_API)
}

export function metricDevices(params) {
  const query = {
    query: {
      metricDevices: {
        __args: params,
        ...METRIC_DATA
      }
    },
  };
  return handleQuery(query, DASHBOARD_API)
}

// Minimal sequential queue for rawChartData only
let rawChartRequestChain = Promise.resolve();
let rawQueueCancelled = false;
function enqueueRawChartRequest(run) {
  const p = rawChartRequestChain.then(() => {
    if (rawQueueCancelled) {
      // Stop executing further queued tasks
      return Promise.reject(new Error('RAW_CHART_QUEUE_CANCELLED'));
    }
    return run();
  });
  // Keep the chain alive even if a request fails
  rawChartRequestChain = p.catch(() => { });
  return p;
}

// Abort controllers for chart requests
let currentRawChartController = null;
let currentPreviewRawChartController = null;
let rawChartControllers = new Set();
let previewRawChartControllers = new Set();

export function cancelRawChartRequests() {
  try { currentRawChartController && currentRawChartController.abort(); } catch (e) { }
  currentRawChartController = null;
  try {
    for (const c of rawChartControllers) { try { c.abort(); } catch (_) { } }
  } finally {
    rawChartControllers.clear();
  }
  rawQueueCancelled = true;
  rawChartRequestChain = Promise.resolve();
}

export function cancelPreviewRawChartRequests() {
  try { currentPreviewRawChartController && currentPreviewRawChartController.abort(); } catch (e) { }
  currentPreviewRawChartController = null;
  try {
    for (const c of previewRawChartControllers) { try { c.abort(); } catch (_) { } }
  } finally {
    previewRawChartControllers.clear();
  }
}

export function cancelAllChartRequests() {
  cancelRawChartRequests();
  cancelPreviewRawChartRequests();
}

export function rawChartData(params) {
  // starting a new request sequence implicitly resumes the queue
  rawQueueCancelled = false;
  const query = {
    query: {
      rawChartData: {
        __args: params,
        ...RAW_CHART_DATA
      }
    },
  };
  const controller = new AbortController();
  currentRawChartController = controller;
  rawChartControllers.add(controller);
  const request = enqueueRawChartRequest(() => handleQuery(query, DASHBOARD_API, { signal: controller.signal }));
  return request.finally(() => { rawChartControllers.delete(controller); })
}

export function previewRawChartData(params) {
  const query = {
    query: {
      previewRawChartData: {
        __args: params,
        ...RAW_CHART_DATA,
      },
    },
  };
  const controller = new AbortController();
  currentPreviewRawChartController = controller;
  previewRawChartControllers.add(controller);
  const request = handleQuery(query, DASHBOARD_API, { signal: controller.signal });
  return request.finally(() => { previewRawChartControllers.delete(controller); })
}
